import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import axios from "axios";
import { toast } from "sonner";
import { Brain, Heart, Focus, AlertCircle, Zap, Moon, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DailyMetricsModal({ open, onComplete }) {
  const [metrics, setMetrics] = useState({
    mental_readiness: 5,
    confidence_level: 5,
    focus_quality: 5,
    stress_level: 5,
    energy_level: 5,
    sleep_quality: 5,
    motivation: 5
  });
  const [submitting, setSubmitting] = useState(false);

  const metricsConfig = [
    { key: 'mental_readiness', label: 'Mental Readiness', icon: Brain, color: 'text-purple-600', description: 'How mentally prepared do you feel today?' },
    { key: 'confidence_level', label: 'Confidence Level', icon: TrendingUp, color: 'text-blue-600', description: 'How confident are you in your abilities?' },
    { key: 'focus_quality', label: 'Focus Quality', icon: Focus, color: 'text-green-600', description: 'How well can you concentrate and stay focused?' },
    { key: 'stress_level', label: 'Stress Level', icon: AlertCircle, color: 'text-orange-600', description: 'How stressed or anxious do you feel?' },
    { key: 'energy_level', label: 'Energy Level', icon: Zap, color: 'text-yellow-600', description: 'How energetic do you feel?' },
    { key: 'sleep_quality', label: 'Sleep Quality', icon: Moon, color: 'text-indigo-600', description: 'How well did you sleep last night?' },
    { key: 'motivation', label: 'Motivation', icon: Heart, color: 'text-red-600', description: 'How motivated are you to train and compete?' }
  ];

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/daily-metrics`, metrics, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Daily metrics submitted successfully!");
      onComplete();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("You've already submitted metrics today");
        onComplete();
      } else {
        toast.error("Failed to submit metrics. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onComplete(); }}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto mx-3 sm:mx-auto rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl">Daily Check-In</DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            Rate each aspect (1-10) to track your mental performance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 sm:space-y-6 py-2 sm:py-4">
          {metricsConfig.map((metric) => {
            const Icon = metric.icon;
            return (
              <div key={metric.key} className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${metric.color}`} />
                  <Label className="text-sm sm:text-base font-semibold">{metric.label}</Label>
                </div>
                <p className="text-xs sm:text-sm text-gray-600">{metric.description}</p>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-xs sm:text-sm text-gray-500">
                    <span>Low (1)</span>
                    <span className="font-semibold text-base sm:text-lg text-gray-900">{metrics[metric.key]}</span>
                    <span>High (10)</span>
                  </div>
                  <Slider
                    value={[metrics[metric.key]]}
                    onValueChange={(value) => setMetrics({ ...metrics, [metric.key]: value[0] })}
                    min={1}
                    max={10}
                    step={1}
                    className="w-full [&_[role=slider]]:h-6 [&_[role=slider]]:w-6 touch-none"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            onClick={onComplete}
            variant="outline"
            className="w-1/3 h-12"
            size="lg"
          >
            Close
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-2/3 bg-sky-600 hover:bg-sky-700 h-12"
            size="lg"
          >
            {submitting ? "Submitting..." : "Submit Check-In"}
          </Button>
        </div>

        <p className="text-[10px] sm:text-xs text-gray-500 text-center">
          Submit metrics to track performance trends
        </p>
      </DialogContent>
    </Dialog>
  );
}
